import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FiSave,
  FiPlus,
  FiX,
} from "react-icons/fi";
import { confirmAndDelete } from "../../utils/confirmAndDelete";
import { RiDeleteBinLine, RiEditLine } from "react-icons/ri";

const BenefitCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 animate-pulse">
    <div className="h-48 bg-gray-300"></div>
    <div className="p-6 space-y-4">
      <div className="h-5 bg-gray-200 rounded w-3/4"></div>
      <div className="flex gap-2">
        <div className="flex-1 h-10 bg-gray-200 rounded-lg"></div>
        <div className="flex-1 h-10 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  </div>
);

const AdminSportsAcademy = () => {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    image: "",
  });

  // Fetch all benefits on mount
  useEffect(() => {
    fetchBenefits();
  }, []);

  const fetchBenefits = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/benefit/get-all-benefits`
      );
      if (data?.data) {
        setBenefits(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Upload image to Cloudinary
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage("Please upload a valid image file.");
      return;
    }

    try {
      setUploading(true);
      setMessage("Uploading image...");

      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append(
        "upload_preset",
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
      );

      const uploadResponse = await axios.post(
        process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
        formDataUpload
      );

      const imageUrl = uploadResponse.data.secure_url;
      setFormData({ ...formData, image: imageUrl });
      setMessage("Image uploaded successfully ✅");
    } catch (error) {
      console.error(error);
      setMessage("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // Open modal for create/edit
  const openModal = (benefit = null) => {
    if (benefit) {
      setEditingBenefit(benefit);
      setFormData({
        title: benefit.title,
        image: benefit.image,
      });
    } else {
      setEditingBenefit(null);
      setFormData({
        title: "",
        image: "",
      });
    }
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingBenefit(null);
    setFormData({ title: "", image: "" });
    setMessage(null);
  };

  // Handle create/update
  const handleSave = async (e) => {
    setSaving(true);
    e.preventDefault();
    try {
      if (editingBenefit) {
        // Update existing benefit
        const { data } = await axios.put(
          `${process.env.REACT_APP_API_URL}/benefit/update-benefit/${editingBenefit._id}`,
          formData
        );
        setMessage(data.message || "Benefit updated successfully!");
      } else {
        // Create new benefit
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/benefit/create-benefit`,
          formData
        );
        setMessage(data.message || "Benefit created successfully!");
      }

      setTimeout(() => setMessage(null), 3000);
      fetchBenefits();
      closeModal();
      setSaving(false);
    } catch (err) {
      setMessage(err.response?.data?.message || "Something went wrong.");
      setSaving(false);
    }
  };

  // Handle delete with SweetAlert
  const handleDelete = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${process.env.REACT_APP_API_URL}/benefit/delete-benefit`,
    });

    if (isConfirmed) {
      try {
        await fetchBenefits();

        Swal.fire({
          title: "Deleted!",
          text: "The benefit has been deleted.",
          icon: "success",
          confirmButtonColor: "#10B981",
        });
      } catch (err) {
        console.error("Error deleting benefit:", err);
        Swal.fire({
          title: "Error!",
          text: "Something went wrong.",
          icon: "error",
          confirmButtonColor: "#EF4444",
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-transparent p-6">
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
            Sports Academy Benefits
          </h1>
          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 bg-primaryBtnColor text-white px-4 py-2 rounded-lg font-semibold hover:bg-primaryBtnHoverColor transition-colors"
          >
            <FiPlus size={18} />
            Add Benefit
          </button>
        </div>

        {message && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600 font-medium">{message}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <BenefitCardSkeleton key={i} />
              ))
            : benefits.map((benefit) => (
                <div
                  key={benefit._id}
                  className="bg-gradient-to-br from-[#2A0A35] to-[#0A1F3D] p-6 rounded-2xl shadow-lg text-center flex flex-col items-center hover:scale-[1.03] transition-transform duration-300"
                >
                  {/* Circular Image */}
                  <div className="w-28 h-28 rounded-full overflow-hidden mb-4">
                    <img
                      src={benefit.image}
                      alt={benefit.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Title */}
                  <h2 className="text-white font-semibold text-lg">
                    {benefit.title}
                  </h2>

                  {/* Optional edit/delete icons */}
                  <div className="absolute top-3 right-3 flex space-x-2">
                    <button
                      onClick={() => openModal(benefit)}
                      className="bg-white/80 p-2 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors"
                    >
                      <RiEditLine />
                    </button>
                    <button
                      onClick={() => handleDelete(benefit._id)}
                      className="bg-white/80 p-2 rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                    >
                      <RiDeleteBinLine />
                    </button>
                  </div>
                </div>
              ))}
        </div>

        {/* Empty state */}
        {!loading && benefits.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiPlus size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No benefits yet
            </h3>
            <p className="text-gray-500">
              Click "Add Benefit" to create your first sports academy benefit.
            </p>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg border border-primaryTextColor shadow-xl">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-primaryTextColor">
                  {editingBenefit ? "Edit Benefit" : "Add New Benefit"}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-primaryBtnHoverColor"
                >
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm text-secondaryTextColor mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg p-3"
                    placeholder="Enter benefit title"
                    required
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm text-secondaryTextColor mb-2">
                    Image
                  </label>

                  {formData.image && (
                    <div className="relative w-32 h-32 mb-2">
                      <img
                        src={formData.image}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg"
                      />
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="w-full border border-gray-300 rounded-lg p-2 text-primaryTextColor"
                  />
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="bg-gray-200 text-secondaryTextColor py-2 px-4 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!formData.title || !formData.image || uploading}
                    className="bg-primaryBtnColor text-white w-36 flex items-center justify-center py-2 px-4 rounded-lg hover:bg-primaryBtnHoverColor disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading || saving ? (
                      <div className="spinner w-full"></div>
                    ) : (
                      <>
                        <FiSave size={18} className="mr-2" />
                        {editingBenefit ? "Update" : "Create"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSportsAcademy;
